// utils/FileUpload.js
import AWS from 'aws-sdk';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import ExcelJS from 'exceljs';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import { toast } from "react-toastify";
import { getFileIdByName, resetFileIdByName } from "../state/actions/getFileIdByNameAction";
import { updateFileUrl } from "../state/actions/updateFileUrlAction";
import { getFileStatus, resetgetfilestatus } from "../state/actions/getUploadFileStatusAction";

GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist/build/pdf.worker.min.js';

AWS.config.update({
    region: process.env.REACT_APP_AWS_REGION,
    credentials: {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
    }
});

const myBucket = new AWS.S3({
    useAccelerateEndpoint: true
});

export const checkPasswordProtection = (file) => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onload = async (e) => {
            try {
                const typedArray = new Uint8Array(e.target.result);
                const loadingTask = getDocument({ data: typedArray });

                await loadingTask.promise.then(
                    () => resolve({ isProtected: false, isCorrupted: false }),
                    (error) => {
                        if (error.name === 'PasswordException') {
                            resolve({ isProtected: true, isCorrupted: false });
                        } else if (error.name === 'InvalidPDFException') {
                            resolve({ isProtected: false, isCorrupted: true });
                        } else {
                            reject(error);
                        }
                    }
                );
            } catch (error) {
                reject(error);
            }
        };

        fileReader.readAsArrayBuffer(file);
    });
};

export const checkExcelFile = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                // Security: migrated from `xlsx` (had unfixable HIGH CVEs) to `exceljs`.
                // Same validation purpose: load the file and reject if parsing fails.
                const workbook = new ExcelJS.Workbook();
                await workbook.xlsx.load(e.target.result);

                resolve({ isProtected: false, isCorrupted: false });
            } catch (error) {
                const msg = (error && error.message) || '';
                if (msg.includes('password-protected') || msg.includes('encrypted')) {
                    resolve({ isProtected: true, isCorrupted: false });
                } else {
                    resolve({ isProtected: false, isCorrupted: true });
                }
            }
        };

        reader.onerror = () => {
            resolve({ isProtected: false, isCorrupted: true });
        };

        reader.readAsArrayBuffer(file);
    });
};

export const checkWordFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = function (e) {
            try {
                const content = e.target.result;
                const zip = new PizZip(content);
                new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });

                resolve({ isProtected: false, isCorrupted: false });
            } catch (error) {
                resolve({ isProtected: true, isCorrupted: true });
            }
        };

        reader.onerror = function (e) {
            reject(e);
        };

        reader.readAsArrayBuffer(file);
    });
};

export const checkImageCorruption = (file) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(false);
        img.onerror = () => resolve(true);

        const reader = new FileReader();
        reader.onload = e => img.src = e.target.result;
        reader.onerror = e => reject(e);
        reader.readAsDataURL(file);
    });
};

export const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read the file.'));
        reader.readAsText(file);
    });
};

export const checkCorruption = (content) => {
    return /[\x00-\x08\x0E-\x1F]/.test(content);
};

export const checkForProtection = (content) => {
    return content.startsWith("PROTECTED:");
};

export const uploadFilesToS3 = async (files, token, dispatch, fileList) => {
    const multipartUploadThreshold = 5 * 1024 * 1024; // 5 MB

    const uploads = Array.from(files).map(async (file) => {
        const dateTimePrefix = new Date().toISOString().replace(/[^0-9]/g, "");
        const fileNameParts = file.name.split(".");
        const newFilename = `${fileNameParts[0]}-${dateTimePrefix}.${fileNameParts.pop()}`;

        if (file.size <= multipartUploadThreshold) {
            const uploadParams = {
                Bucket: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
                Key: newFilename,
                Body: file,
                ContentType: file.type,
            };
            return myBucket.upload(uploadParams).promise();
        } else {
            const multipartParams = {
                Bucket: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
                Key: newFilename,
                ContentType: file.type,
            };
            const multipartUpload = await myBucket.createMultipartUpload(multipartParams).promise();
            const partSize = 5 * 1024 * 1024;
            const partsCount = Math.ceil(file.size / partSize);
            const uploadPartsPromises = [];

            for (let i = 1; i <= partsCount; i++) {
                const start = (i - 1) * partSize;
                const end = i * partSize;
                const partParams = {
                    Body: file.slice(start, end),
                    Bucket: multipartParams.Bucket,
                    Key: multipartParams.Key,
                    PartNumber: i,
                    UploadId: multipartUpload.UploadId,
                };
                uploadPartsPromises.push(myBucket.uploadPart(partParams).promise());
            }

            const uploadedParts = await Promise.all(uploadPartsPromises);
            const completeParams = {
                Bucket: multipartParams.Bucket,
                Key: multipartParams.Key,
                MultipartUpload: {
                    Parts: uploadedParts.map((part, index) => ({
                        ETag: part.ETag,
                        PartNumber: index + 1,
                    })),
                },
                UploadId: multipartUpload.UploadId,
            };

            return myBucket.completeMultipartUpload(completeParams).promise();
        }
    });

    try {
        const results = await Promise.all(uploads);
        let newArr = results.map(itemA => {
            let baseFileNameA = itemA.Key.split('-')[0];
            const itemB = fileList.find(itemB => itemB.original_name.startsWith(baseFileNameA));
            return itemB ? {
                location: itemA.Location,
                key: itemA.key,
                name: itemB.original_name,
                file_id: itemB.file_id,
            } : null;
        }).filter(item => item !== null);

        if (newArr.length) {
            const req = {
                file_upload: newArr.map((f) => ({
                    file_id: f.file_id,
                    link: f.location,
                }))
            };
            dispatch(updateFileUrl(req, token));
            newArr.map((arr) => { dispatch(getFileStatus(arr.file_id, token)); });
        }

        return newArr;
    } catch (error) {
        console.error('Error uploading files:', error);
        throw error;
    }
};
